/* Зона прикрепления файлов к документам трансмиттала.
 *
 * Файлы выбираются пачкой или папкой, и система раскидывает их по карточкам
 * по имени: «<№ документа>_<роль>.<расш>», где роль — PDF (основной),
 * дополнительный или Исходник (замечания тестирования 17.08, п. 3–4).
 * Несопоставленный файл не грузится молча — он остаётся в отчёте с причиной.
 *
 * Загрузка — тот же трёхшаговый конвейер, что в карточке документа: подпись →
 * PUT напрямую в хранилище → регистрация версии. Байты через приложение не
 * идут. Файлы грузятся по одному: у каждого свой отказ и своя строка отчёта,
 * пакетная транзакция здесь невозможна и не обещается.
 */
import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { request } from '@/data/api'
import { qk, useSub } from '@/data/queries'
import { Button, Spinner } from '@/ui'
import { Icon } from '@/ds/icons'
import { t, type Lang } from '@/i18n/lang'

export interface TrmDocRef {
  no: string
  rev: string
}

type FileKind = 'main' | 'additional' | 'original'

/* Суффикс имени → роль файла. Словарь совпадает с маской справочника
   кодирования «[Документ]_[Роль].pdf» (см. FileCommands и V15). */
const KIND_BY_SUFFIX: Record<string, FileKind> = {
  pdf: 'main',
  дополнительный: 'additional',
  исходник: 'original',
}

export interface ParsedName {
  doc: string
  kind: FileKind
}

/** «2100-PI-DR-221_Исходник.dwg» → документ и роль; не по маске — null. */
export function parseTrmFileName(name: string): ParsedName | null {
  const dot = name.lastIndexOf('.')
  if (dot <= 0) return null
  const base = name.slice(0, dot)
  const us = base.lastIndexOf('_')
  if (us <= 0) return null
  const kind = KIND_BY_SUFFIX[base.slice(us + 1).toLowerCase()]
  return kind ? { doc: base.slice(0, us), kind } : null
}

const KIND_RU: Record<FileKind, { ru: string; en: string }> = {
  main: { ru: 'основной', en: 'main' },
  additional: { ru: 'дополнительный', en: 'additional' },
  original: { ru: 'исходник', en: 'original' },
}

interface Row {
  file: File
  doc: TrmDocRef | null
  kind: FileKind | null
  status: 'wait' | 'up' | 'ok' | 'error' | 'unmatched'
  note: string
}

const SHA_LIMIT = 100 * 1024 * 1024

async function sha256Hex(file: File): Promise<string | null> {
  if (file.size > SHA_LIMIT) return null
  const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer())
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('')
}

async function uploadOne(doc: TrmDocRef, kind: FileKind, file: File): Promise<void> {
  const base = '/docs/' + encodeURIComponent(doc.no) + '/revisions/' + encodeURIComponent(doc.rev)
  const contentType = file.type || 'application/octet-stream'
  const sha256 = await sha256Hex(file)
  const intent = await request<{ uploadUrl: string; objectKey: string }>(
    'POST', base + '/upload-intent',
    { fileName: file.name, contentType, size: file.size, kind },
  )
  /* Голый fetch: ссылка уже подписана, лишний заголовок ломает подпись. */
  const res = await fetch(intent.uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': contentType } })
  if (!res.ok) throw new Error(`PUT ${res.status}`)
  await request('POST', base + '/files', { objectKey: intent.objectKey, sha256, kind },
    { idempotencyKey: crypto.randomUUID() })
}

/**
 * Файл самого трансмиттала: сопроводительное письмо, подписанный экземпляр.
 *
 * <p>Те же три шага, что у файла документа, и по той же причине: байты идут
 * прямо в хранилище по подписанной ссылке, а сервер только выдаёт её и
 * сверяет появившийся объект.
 *
 * <p>Имя файла по маске здесь НЕ проверяется: маска описывает имя документа
 * («[Документ]_[Роль].pdf»), а письмо приходит от контрагента со своим именем.
 */
export async function uploadTrmFile(code: string, file: File, kind: 'cover' | 'signed' | 'other'): Promise<void> {
  const base = '/transmittals/' + encodeURIComponent(code) + '/files'
  const contentType = file.type || 'application/octet-stream'
  const sha256 = await sha256Hex(file)
  const intent = await request<{ uploadUrl: string; objectKey: string }>(
    'POST', base + '/upload-intent',
    { fileName: file.name, contentType, size: file.size, kind },
  )
  const res = await fetch(intent.uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': contentType } })
  if (!res.ok) throw new Error(`PUT ${res.status}`)
  await request('POST', base, { objectKey: intent.objectKey, sha256, kind },
    { idempotencyKey: crypto.randomUUID() })
}

/* Приложение к замечанию: выкопировка с разметкой, расчёт, выдержка из
   норматива; со стороны подрядчика — приложение к ответу. Конвейер тот же
   трёхшаговый: подпись → PUT в хранилище → регистрация. */
export async function uploadCrsFile(code: string, file: File, kind: 'comment' | 'answer'): Promise<void> {
  const base = '/crs/' + encodeURIComponent(code) + '/files'
  const contentType = file.type || 'application/octet-stream'
  const sha256 = await sha256Hex(file)
  const intent = await request<{ uploadUrl: string; objectKey: string }>(
    'POST', base + '/upload-intent',
    { fileName: file.name, contentType, size: file.size, kind },
  )
  const res = await fetch(intent.uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': contentType } })
  if (!res.ok) throw new Error(`PUT ${res.status}`)
  await request('POST', base, { objectKey: intent.objectKey, sha256, kind },
    { idempotencyKey: crypto.randomUUID() })
}

/** Приложения замечания: список со ссылками и кнопка «приложить». */
export function CrsFilesZone({ code, lang, kind, disabled }: {
  code: string
  lang: Lang
  /** Роль приложения со стороны вызывающего; подрядчику сервер всё равно поставит «ответ». */
  kind: 'comment' | 'answer'
  disabled?: boolean
}) {
  const path = `/crs/${encodeURIComponent(code)}/files`
  const files = useSub<{ files: { kind: string; name: string; size: number; uploadedBy: string | null; url: string }[] }>(path)
  const client = useQueryClient()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const pick = () => {
    const input = document.createElement('input')
    input.type = 'file'
    input.onchange = async () => {
      const file = input.files?.[0]
      if (!file) return
      setBusy(true)
      setError('')
      try {
        await uploadCrsFile(code, file, kind)
        await client.invalidateQueries({ queryKey: qk.sub(path) })
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e))
      } finally {
        setBusy(false)
      }
    }
    input.click()
  }

  /* Ошибиться файлом так же легко, как в письме: приложили не ту выкопировку,
     не тот расчёт. Снять было нечем — до выпуска замечания это исправимо. */
  const drop = async (name: string) => {
    const reason = window.prompt(t(lang, 'Причина снятия приложения', 'Why remove the attachment?'), '')
    if (reason == null || reason.trim() === '') return
    setBusy(true)
    setError('')
    try {
      await request('DELETE', `${path}?name=${encodeURIComponent(name)}&reason=${encodeURIComponent(reason.trim())}`,
        undefined, { idempotencyKey: crypto.randomUUID() })
      await client.invalidateQueries({ queryKey: qk.sub(path) })
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const rows = files.data?.files ?? []
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      {rows.map((f) => (
        <div key={f.name} style={{ display: 'flex', alignItems: 'center', gap: 8, font: 'var(--font-ui)', fontSize: 'var(--text-sm)' }}>
          <Icon name="paperclip" size={13} />
          <a href={f.url} target="_blank" rel="noreferrer" style={{ color: 'var(--text-link)' }}>{f.name}</a>
          <span style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>
            {f.kind === 'answer' ? t(lang, 'к ответу', 'to the answer') : t(lang, 'к замечанию', 'to the comment')}
            {f.uploadedBy ? ' · ' + f.uploadedBy : ''}
          </span>
          {disabled ? null : (
            <Button variant="ghost" size="sm" iconLeft={<Icon name="x" size={12} />} disabled={busy}
                    title={t(lang, 'Снять приложение. Выпущенное замечание сервер не отдаст.',
                                  'Remove the attachment. The server refuses once the comment is issued.')}
                    onClick={() => void drop(f.name)}>{t(lang, 'Снять', 'Remove')}</Button>
          )}
        </div>
      ))}
      {disabled ? null : (
        <div>
          <Button variant="ghost" size="sm" iconLeft={<Icon name="paperclip" size={13} />} disabled={busy} onClick={pick}>
            {busy ? t(lang, 'Загружаем…', 'Uploading…') : t(lang, 'Приложить файл', 'Attach a file')}
          </Button>
        </div>
      )}
      {error ? <div style={{ font: 'var(--font-caption)', color: 'var(--danger-ink)' }}>{error}</div> : null}
    </div>)
}

export function TrmFilesZone({ docs, lang, disabled }: { docs: TrmDocRef[]; lang: Lang; disabled?: boolean }) {
  const client = useQueryClient()
  const [rows, setRows] = useState<Row[]>([])
  const [busy, setBusy] = useState(false)

  const matchRows = (files: File[]): Row[] => files.map((file) => {
    const parsed = parseTrmFileName(file.name)
    if (!parsed) {
      return { file, doc: null, kind: null, status: 'unmatched', note: t(lang, 'имя не по маске «№_роль.расширение»', 'name does not follow “№_role.ext”') }
    }
    const doc = docs.find((d) => d.no.toLowerCase() === parsed.doc.toLowerCase()) ?? null
    if (!doc) {
      return { file, doc: null, kind: parsed.kind, status: 'unmatched', note: t(lang, `документа ${parsed.doc} нет среди выбранных`, `${parsed.doc} is not among the picked documents`) }
    }
    return { file, doc, kind: parsed.kind, status: 'wait', note: '' }
  })

  const run = async (files: File[]) => {
    if (!files.length) return
    const next = matchRows(files)
    setRows(next)
    setBusy(true)
    try {
      for (let i = 0; i < next.length; i++) {
        const row = next[i]!
        if (row.status !== 'wait' || !row.doc || !row.kind) continue
        setRows((rs) => rs.map((r, j) => (j === i ? { ...r, status: 'up' } : r)))
        try {
          await uploadOne(row.doc, row.kind, row.file)
          setRows((rs) => rs.map((r, j) => (j === i ? { ...r, status: 'ok' } : r)))
        } catch (e) {
          setRows((rs) => rs.map((r, j) => (j === i
            ? { ...r, status: 'error', note: e instanceof Error ? e.message : String(e) } : r)))
        }
      }
    } finally {
      setBusy(false)
      await Promise.all([qk.all('docs'), qk.all('vdocs'), qk.all('audit')]
        .map((key) => client.invalidateQueries({ queryKey: key })))
    }
  }

  const pickInput = (dir: boolean) => {
    const input = document.createElement('input')
    input.type = 'file'
    input.multiple = true
    // Выбор папки: система раскидает её содержимое по номерам в именах.
    if (dir) input.setAttribute('webkitdirectory', '')
    input.onchange = () => { void run(Array.from(input.files ?? [])) }
    input.click()
  }

  const ICON: Record<Row['status'], { name: string; color: string }> = {
    wait: { name: 'clock', color: 'var(--text-subtle)' },
    up: { name: 'upload', color: 'var(--accent-ink)' },
    ok: { name: 'check', color: 'var(--success-ink)' },
    error: { name: 'circle-alert', color: 'var(--danger-ink)' },
    unmatched: { name: 'file-warning', color: 'var(--warning-ink)' },
  }

  return (
    <div style={{ marginTop: 12, border: '1px dashed var(--border-default)', borderRadius: 'var(--radius-md)', padding: '10px 12px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ font: 'var(--font-eyebrow)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--text-subtle)' }}>{t(lang, 'Файлы к документам', 'Document files')}</span>
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <Button variant="secondary" size="sm" iconLeft={<Icon name="upload" size={13} />} disabled={disabled || busy || !docs.length}
                  title={docs.length ? undefined : t(lang, 'Сначала выберите документы', 'Pick documents first')}
                  onClick={() => pickInput(false)}>{t(lang, 'Прикрепить файлы', 'Attach files')}</Button>
          <Button variant="secondary" size="sm" iconLeft={<Icon name="folder" size={13} />} disabled={disabled || busy || !docs.length}
                  title={docs.length ? undefined : t(lang, 'Сначала выберите документы', 'Pick documents first')}
                  onClick={() => pickInput(true)}>{t(lang, 'Загрузить папку', 'Upload a folder')}</Button>
        </span>
      </div>
      <div style={{ marginTop: 6, font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>
        {t(lang,
          'Файлы раскидываются по выбранным карточкам по имени: «№ документа_PDF.pdf» — основной, «…_дополнительный.pdf», «…_Исходник.dwg» (расширение исходника любое).',
          'Files are matched to the picked documents by name: “doc №_PDF.pdf” is the main file, “…_дополнительный.pdf”, “…_Исходник.dwg” (any source extension).')}
      </div>
      {rows.length ? (
        <div style={{ marginTop: 8, maxHeight: 160, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
          {rows.map((r, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, font: 'var(--font-ui)', fontSize: 'var(--text-sm)' }}>
              {/* Идущая загрузка вертится, а не стоит: статичная стрелка неотличима
                  от зависшего PUT на 100 МБ. Остальные статусы — состояния, не процессы. */}
              <span style={{ display: 'inline-flex', color: ICON[r.status].color }}>
                {r.status === 'up' ? <Spinner size={13} /> : <Icon name={ICON[r.status].name} size={13} />}
              </span>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--text-body)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 260 }}>{r.file.name}</span>
              {r.doc && r.kind ? (
                <span style={{ color: 'var(--text-subtle)' }}>{'→ '}{r.doc.no} · {t(lang, KIND_RU[r.kind].ru, KIND_RU[r.kind].en)} · {t(lang, 'рев.', 'rev.')} {r.doc.rev}</span>
              ) : null}
              {r.note ? <span style={{ color: r.status === 'error' ? 'var(--danger-ink)' : 'var(--warning-ink)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.note}</span> : null}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  )
}
